import "react-image-crop/dist/ReactCrop.css";
import "./LogoUploader.scss";

import Button from "components/Button/Button";
import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import type { Crop, PixelCrop } from "react-image-crop";
import ReactCrop, { centerCrop, convertToPixelCrop } from "react-image-crop";
import { useUploadOrganizationLetterheadMutation } from "services/misc-api";

import {
  canvasPreview,
  canvasToBlob,
  checkImageTransparency,
  MIN_HEIGHT,
  MIN_WIDTH,
} from "./LogoCropper";

const ACCEPTED_FILE_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

export const LogoUploader = () => {
  const [uploadLetterhead] = useUploadOrganizationLetterheadMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [hasAlpha, setHasAlpha] = useState(false);
  const [cropError, setCropError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = e.currentTarget;

      // create a centered crop with default dimensions,
      // but without enforcing an aspect ratio
      const initialCrop = {
        unit: "%" as const,
        width: 80,
        height: 80,
        x: 10,
        y: 10,
      };

      const crop = centerCrop(initialCrop, naturalWidth, naturalHeight);

      setCrop(crop);
      // convert percentage crop to pixel values
      const pixelCrop = convertToPixelCrop(crop, naturalWidth, naturalHeight);
      setCompletedCrop(pixelCrop);

      // check if image has transparency
      if (fileType === "image/png" && imgRef.current) {
        const hasTransparency = checkImageTransparency(imgRef.current);
        setHasAlpha(hasTransparency);
      }
    },
    [fileType],
  );

  useEffect(() => {
    if (!completedCrop || !imgRef.current || !previewCanvasRef.current) return;

    // PNG files should preserve transparency, other formats will get a white background
    const preserveTransparency = fileType === "image/png" && hasAlpha;

    canvasPreview(
      imgRef.current,
      previewCanvasRef.current,
      completedCrop,
      preserveTransparency,
    );
  }, [completedCrop, fileType, hasAlpha]);

  // validate crop dimensions
  useEffect(() => {
    if (!completedCrop || !imgRef.current) {
      setCropError(null);
      return;
    }

    const { naturalWidth, naturalHeight } = imgRef.current;
    const actualWidth = (completedCrop.width / 100) * naturalWidth;
    const actualHeight = (completedCrop.height / 100) * naturalHeight;

    if (actualWidth < MIN_WIDTH || actualHeight < MIN_HEIGHT) {
      setCropError(
        `Image or selection is too small. Choose a larger image or crop to a larger area. Minimum size: ${MIN_WIDTH}×${MIN_HEIGHT}px`,
      );
    } else {
      setCropError(null);
    }
  }, [completedCrop]);

  const getCroppedImage = useCallback(async (): Promise<Blob> => {
    if (!previewCanvasRef.current) {
      throw new Error("Canvas preview not ready");
    }

    // use PNG for transparent images, JPEG for non-transparent
    const outputFormat = hasAlpha ? "image/png" : "image/jpeg";

    return canvasToBlob(previewCanvasRef.current, outputFormat);
  }, [hasAlpha]);

  const handleFileSelect = useCallback((files: File[]) => {
    if (!files?.length) return;

    const file = files[0];
    setFileType(file.type);

    const reader = new FileReader();

    reader.onload = () => {
      setImageSrc(reader.result as string);
    };

    reader.readAsDataURL(file);
  }, []);

  const handleManualFileSelect = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".jpg,.jpeg,.png";
    input.multiple = false;

    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement)?.files;
      if (files?.length) {
        handleFileSelect([files[0]]);
      }
    };

    // trigger file selection dialog
    input.click();
  }, [handleFileSelect]);

  const handleUpload = useCallback(async () => {
    if (!imageSrc || !completedCrop || cropError) return;

    setIsUploading(true);

    try {
      // get the cropped image as a blob - the preview canvas already has the right crop
      const croppedBlob = await getCroppedImage();

      // create a File from the Blob - use PNG format for transparency
      const fileExtension = hasAlpha ? "png" : "jpg";
      const mimeType = hasAlpha ? "image/png" : "image/jpeg";

      const file = new File([croppedBlob], `logo.${fileExtension}`, {
        type: mimeType,
      });

      await uploadLetterhead({
        file,
      }).unwrap();

      KaboodleToaster.success(
        "Logo uploaded successfully. Changes will take effect momentarily.",
      );

      setImageSrc(null);
      setFileType(null);
      setHasAlpha(false);
    } catch (error) {
      console.error("Failed to upload logo:", error);
      KaboodleToaster.error(
        error instanceof Error ? error.message : "Failed to upload logo",
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    imageSrc,
    uploadLetterhead,
    getCroppedImage,
    completedCrop,
    hasAlpha,
    cropError,
  ]);

  const cancelCrop = useCallback(() => {
    setImageSrc(null);
    setFileType(null);
    setHasAlpha(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileSelect,
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
    noClick: true, // prevent click from opening the file dialog
    disabled: !!imageSrc || isUploading,
  });

  return (
    <div className="logo-uploader">
      {imageSrc ? (
        <div className="logo-uploader__cropper">
          <p className="logo-uploader__instructions">
            Adjust the crop to fit your logo
            <br />
            <span className="logo-uploader__instructions-subtext">
              Any dimensions are supported, including square logos
            </span>
          </p>
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            // no aspect ratio constraint to allow any dimensions
            className="logo-uploader__crop-container"
            minWidth={30} // minimum percentage width
            minHeight={30} // minimum percentage height
            keepSelection={true}
            ruleOfThirds={true}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Logo to crop"
              onLoad={onImageLoad}
              style={{ maxWidth: "100%" }}
              crossOrigin="anonymous"
            />
          </ReactCrop>

          {cropError && <p className="logo-uploader__error">{cropError}</p>}

          <div className="logo-uploader__preview">
            <p className="logo-uploader__preview-text">
              Preview:{" "}
              {fileType === "image/png" && hasAlpha && (
                <span className="logo-uploader__transparency-note">
                  (with transparency)
                </span>
              )}
            </p>
            <div
              className={`logo-uploader__preview-wrapper ${hasAlpha ? "has-transparency" : ""}`}
            >
              <canvas
                ref={previewCanvasRef}
                className="logo-uploader__preview-canvas"
              />
            </div>
          </div>

          <div className="logo-uploader__actions flex jc-c mt-4">
            <Button onClick={cancelCrop} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={isUploading || !completedCrop || !!cropError}
            >
              {isUploading ? "Uploading..." : "Upload Logo"}
            </Button>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`logo-uploader__dropzone ${
            isDragActive ? "logo-uploader__dropzone--active" : ""
          }`}
        >
          <input {...getInputProps()} ref={fileInputRef} />

          <div className="logo-uploader__placeholder">
            {isUploading ? (
              <div className="logo-uploader__loading">Uploading...</div>
            ) : (
              <>
                <p className="logo-uploader__drag-text">
                  {isDragActive
                    ? "Drop file to upload"
                    : "Drag file here to upload"}
                </p>
                <p className="logo-uploader__dimensions-text">
                  Supports any dimensions, including square logos
                </p>
                <div className="logo-uploader__actions flex jc-c">
                  <Button
                    onClick={handleManualFileSelect}
                    className="btn-secondary btn-sm"
                    disabled={isUploading}
                  >
                    Select File
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
